import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  otherGroupsFormEstablishmentTypes,
  schoolFormEstablishmentTypes,
} from '../registrationFormComponents/data';

const emptySelection = () => ({
  allData: false,
  schoolTypes: [],
  otherGroupTypes: [],
  experienceTypes: [],
});

const ExportData = ({
  onExport,
  adminData,
  allowedSections = ['school', 'other-group'],
  specificSchoolTypes = null,
  specificOtherGroupTypes = null,
  showAllDataOption = false,
}) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState({ school: true, otherGroup: true });
  const [selectedOptions, setSelectedOptions] = useState(emptySelection);

  const allowedSectionsKey = allowedSections.join(',');
  const schoolTypesKey = specificSchoolTypes?.join(',') ?? '';
  const otherGroupTypesKey = specificOtherGroupTypes?.join(',') ?? '';
  const canShowSchool =
    adminData?.assignedRegistrationTypes?.includes('school') &&
    allowedSectionsKey.split(',').includes('school');
  const canShowOtherGroup =
    adminData?.assignedRegistrationTypes?.includes('other-group') &&
    allowedSectionsKey.split(',').includes('other-group');

  const schoolTypes = specificSchoolTypes
    ? schoolFormEstablishmentTypes.filter((item) => specificSchoolTypes.includes(item.value))
    : schoolFormEstablishmentTypes;
  const otherGroupTypes = specificOtherGroupTypes
    ? otherGroupsFormEstablishmentTypes.filter((item) =>
        specificOtherGroupTypes.includes(item.value),
      )
    : otherGroupsFormEstablishmentTypes;

  useEffect(() => {
    setSelectedOptions(emptySelection());
  }, [canShowSchool, canShowOtherGroup, schoolTypesKey, otherGroupTypesKey]);

  useEffect(() => {
    onExport?.(selectedOptions);
  }, [onExport, selectedOptions]);

  const isAllSelected = (items, selected) =>
    items.length > 0 && items.every((item) => selected.includes(item.value));

  const updateSelection = (school, otherGroup) => {
    const allSchoolSelected = !canShowSchool || isAllSelected(schoolTypes, school);
    const allOtherGroupSelected = !canShowOtherGroup || isAllSelected(otherGroupTypes, otherGroup);
    setSelectedOptions({
      allData:
        showAllDataOption &&
        !specificSchoolTypes &&
        !specificOtherGroupTypes &&
        allSchoolSelected &&
        allOtherGroupSelected,
      schoolTypes: school,
      otherGroupTypes: otherGroup,
      experienceTypes: [],
    });
  };

  const toggleAll = () => {
    if (selectedOptions.allData) {
      updateSelection([], []);
    } else {
      updateSelection(
        canShowSchool ? schoolTypes.map((item) => item.value) : [],
        canShowOtherGroup ? otherGroupTypes.map((item) => item.value) : [],
      );
    }
  };

  const toggleSectionSelection = (category, items) => {
    const selected = selectedOptions[category];
    const next = isAllSelected(items, selected) ? [] : items.map((item) => item.value);
    updateSelection(
      category === 'schoolTypes' ? next : selectedOptions.schoolTypes,
      category === 'otherGroupTypes' ? next : selectedOptions.otherGroupTypes,
    );
  };

  const toggleItem = (category, value) => {
    const selected = selectedOptions[category];
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    updateSelection(
      category === 'schoolTypes' ? next : selectedOptions.schoolTypes,
      category === 'otherGroupTypes' ? next : selectedOptions.otherGroupTypes,
    );
  };

  const indicator = (selected) => (
    <span
      aria-hidden="true"
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${selected ? 'bg-teal-40' : 'bg-teal-30'}`}>
      {selected && (
        <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </span>
  );

  const renderSection = (id, title, category, items) => (
    <section key={id} className="rounded-xl border border-teal-10 bg-white px-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          aria-expanded={expandedSections[id]}
          onClick={() => setExpandedSections((current) => ({ ...current, [id]: !current[id] }))}
          className="flex min-w-0 flex-1 items-center gap-2 py-4 text-left font-semibold text-gray-10">
          <svg
            className={`h-4 w-4 shrink-0 transition-transform ${expandedSections[id] ? '' : '-rotate-90'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m6 9 6 6 6-6" />
          </svg>
          {title}
        </button>
        <button
          type="button"
          aria-label={`Tout sélectionner : ${title}`}
          aria-pressed={isAllSelected(items, selectedOptions[category])}
          onClick={() => toggleSectionSelection(category, items)}
          className="rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-10">
          {indicator(isAllSelected(items, selectedOptions[category]))}
        </button>
      </div>
      {expandedSections[id] && (
        <div className="border-t border-gray-40">
          {items.map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={selectedOptions[category].includes(item.value)}
              onClick={() => toggleItem(category, item.value)}
              className="flex w-full items-center justify-between gap-3 border-b border-gray-40 py-3 pl-5 text-left text-gray-10 last:border-b-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-10">
              <span>{item.label}</span>
              {indicator(selectedOptions[category].includes(item.value))}
            </button>
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="flex w-full flex-col gap-4 rounded-xl bg-teal-10/80 p-4">
      {!canShowSchool && !canShowOtherGroup ? (
        <p className="rounded-xl bg-white p-4 text-center text-gray-50">
          No export options available for the current selection.
        </p>
      ) : (
        <>
          {showAllDataOption && (
            <button
              type="button"
              aria-pressed={selectedOptions.allData}
              onClick={toggleAll}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-teal-10 bg-white px-4 py-3 text-left text-gray-10 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-10">
              <span>{t('exportData.allDataOption')}</span>
              {indicator(selectedOptions.allData)}
            </button>
          )}
          {canShowSchool &&
            schoolTypes.length > 0 &&
            renderSection('school', 'SCOLAIRE', 'schoolTypes', schoolTypes)}
          {canShowOtherGroup &&
            otherGroupTypes.length > 0 &&
            renderSection('otherGroup', 'AUTRE GROUPE', 'otherGroupTypes', otherGroupTypes)}
        </>
      )}
    </div>
  );
};

export default ExportData;
